import React, { useState, useEffect } from 'react';
import { Button, Input } from 'reactstrap';
import { getDefaultAppFee, updateDefaultAppFee } from '../../../axios/appFee';
import { useAuthStore } from '../../../zustand-store/auth.store';
import Spinner from '../../../components/Common/Spinner';
import { toast } from 'react-toastify';

const DefaultAppFee = () => {
  const [loader, setLoader] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const locationId = useAuthStore((state) => state.locationId);

  const [defaultAppFee, setDefaultAppFee] = useState('');
  const [defaultAppFeeUnit, setDefaultAppFeeUnit] = useState('amount');
  const [disabled, setDisabled] = useState(true);

  const [stripeAppFeeEnabled, setStripeAppFeeEnabled] = useState(false);
  const [squareAppFeeEnabled, setSquareAppFeeEnabled] = useState(false);

  const [feeId, setFeeId] = useState(null);
  const [unitId, setUnitId] = useState(null);
  const [stripeId, setStripeId] = useState(null);
  const [squareId, setSquareId] = useState(null);

  const onLoad = async () => {
    setLoader(true);
    try {
      const data = await getDefaultAppFee();

      if (data.status === 200 || data.status === 201) {
        const settings = data?.data?.data || [];

        const feeObj = settings.find((s) => s.DEFAULT_APPLICATION_FEE);
        const unitObj = settings.find((s) => s.DEFAULT_APPLICATION_FEE_UNIT);
        const stripeObj = settings.find((s) => s.IS_APP_FEE_FROM_STRIPE_ENABLED);
        const squareObj = settings.find((s) => s.IS_APP_FEE_FROM_SQUARE_ENABLED);

        setDefaultAppFee(feeObj?.DEFAULT_APPLICATION_FEE?.value ?? '');
        setDefaultAppFeeUnit(unitObj?.DEFAULT_APPLICATION_FEE_UNIT?.value ?? 'amount');
        setFeeId(feeObj?.DEFAULT_APPLICATION_FEE?.id ?? null);
        setUnitId(unitObj?.DEFAULT_APPLICATION_FEE_UNIT?.id ?? null);

        setStripeAppFeeEnabled(stripeObj?.IS_APP_FEE_FROM_STRIPE_ENABLED?.value === 'true');
        setStripeId(stripeObj?.IS_APP_FEE_FROM_STRIPE_ENABLED?.id ?? null);

        setSquareAppFeeEnabled(squareObj?.IS_APP_FEE_FROM_SQUARE_ENABLED?.value === 'true');
        setSquareId(squareObj?.IS_APP_FEE_FROM_SQUARE_ENABLED?.id ?? null);
      }
    } catch (err) {
      console.error('Failed to fetch default app fee:', err);
      toast.error('Failed to load default application fee settings.');
    }
    setLoader(false);
  };

  useEffect(() => {
    onLoad();
  }, [locationId]);

  const onSubmit = async () => {
    const confirmSubmit = window.confirm('Are you sure you want to save these changes?');
    if (!confirmSubmit) return;

    setSubmitting(true);
    try {
      const [feeRes, unitRes, stripeRes, squareRes] = await Promise.allSettled([
        updateDefaultAppFee(feeId, 'DEFAULT_APPLICATION_FEE', defaultAppFee),
        updateDefaultAppFee(unitId, 'DEFAULT_APPLICATION_FEE_UNIT', defaultAppFeeUnit),
        updateDefaultAppFee(stripeId, 'IS_APP_FEE_FROM_STRIPE_ENABLED', stripeAppFeeEnabled),
        updateDefaultAppFee(squareId, 'IS_APP_FEE_FROM_SQUARE_ENABLED', squareAppFeeEnabled)
      ]);

      const isSuccess = (res) =>
        res.status === 'fulfilled' && (res.value.status === 200 || res.value.status === 201);

      if (isSuccess(feeRes) && isSuccess(unitRes) && isSuccess(stripeRes) && isSuccess(squareRes)) {
        toast.success('Settings updated successfully');
        setDisabled(true);
        onLoad();
      } else {
        toast.error('Something went wrong while updating settings');
        console.error({ feeRes, unitRes, stripeRes, squareRes });
      }
    } catch (error) {
      toast.error('Unexpected error occurred during update');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {loader && <Spinner setLoading={setLoader} />}

      <div className="d-flex flex-column gap-3 col-12 col-md-8">
        {/* ✅ Stripe Toggle */}
        <div className="row align-items-center">
          <div className="col-6">
            <p className="m-0 fw-bold" style={{ fontSize: '13px' }}>
              Stripe App Fee
            </p>
            <p className="m-0 text-muted">Enable application fee for Stripe payments.</p>
          </div>

          <div className="col-2 d-flex justify-content-end">
            <div className="form-check form-switch form-switch-lg">
              <input
                className="form-check-input"
                type="checkbox"
                checked={stripeAppFeeEnabled}
                onClick={(e) => {
                  setStripeAppFeeEnabled(!e.target.checked);
                  setDisabled(false);
                }}
              />
            </div>
          </div>
        </div>

        {/* ✅ Square Toggle */}
        <div className="row align-items-center">
          <div className="col-6">
            <p className="m-0 fw-bold" style={{ fontSize: '13px' }}>
              Square App Fee
            </p>
            <p className="m-0 text-muted">Enable application fee for Square payments.</p>
          </div>

          <div className="col-2 d-flex justify-content-end">
            <div className="form-check form-switch form-switch-lg">
              <input
                className="form-check-input"
                type="checkbox"
                checked={squareAppFeeEnabled}
                onClick={(e) => {
                  setSquareAppFeeEnabled(!e.target.checked);
                  setDisabled(false);
                }}
              />
            </div>
          </div>
        </div>

        {/* Default Fee */}
        <div className="row">
          <p className="m-0 fw-bold" style={{ fontSize: '13px' }}>
            Default App Fee
          </p>
          <p className="m-0 text-muted">
            This fee will be charged to customers by default when they book.
          </p>

          <div className="mt-2 col-4">
            <Input
              type="number"
              value={defaultAppFee}
              onChange={(e) => {
                setDefaultAppFee(e.target.value);
                setDisabled(false);
              }}
              min="0"
            />
          </div>

          <div className="mt-2 col-4">
            <Input
              type="select"
              value={defaultAppFeeUnit}
              onChange={(e) => {
                setDefaultAppFeeUnit(e.target.value);
                setDisabled(false);
              }}
            >
              <option value="amount">£</option>
              <option value="percentage">%</option>
            </Input>
          </div>
        </div>

        <div className="d-flex align-items-center mt-5 gap-2">
          <Button className="bg-primary" onClick={onSubmit} disabled={disabled || submitting}>
            {submitting ? 'Saving Changes' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DefaultAppFee;
